import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { X, Search } from 'lucide-react';
import { InvoiceStatus } from '../../types';
import { getMonthOptions } from '../../utils/formatters';

interface InvoiceFiltersProps {
  statusFilter: string;
  monthFilter: string;
  memberNameFilter: string;
  onStatusChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onMemberNameChange: (value: string) => void;
  onClearFilters: () => void;
}

export const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  statusFilter,
  monthFilter,
  memberNameFilter,
  onStatusChange,
  onMonthChange,
  onMemberNameChange,
  onClearFilters,
}) => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const monthOptions = getMonthOptions();
  const hasFilters = statusFilter !== '__all__' || monthFilter !== '__all__' || memberNameFilter !== '';

  return (
    <div className="flex flex-wrap items-end gap-4 mb-6" data-testid="invoice-filters">
      <div className="flex-1 min-w-[200px]">
        <Label htmlFor="status-filter">{t('filters.status.label')}</Label>
        <Select value={statusFilter} onValueChange={onStatusChange}>
          <SelectTrigger id="status-filter" data-testid="invoice-filter-status">
            <SelectValue placeholder={t('filters.status.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="invoice-filter-status-all">{t('filters.status.all')}</SelectItem>
            <SelectItem value={InvoiceStatus.PENDING} data-testid="invoice-filter-status-pending">{tCommon('status.pending')}</SelectItem>
            <SelectItem value={InvoiceStatus.UNDER_REVIEW} data-testid="invoice-filter-status-under-review">{tCommon('status.underReview')}</SelectItem>
            <SelectItem value={InvoiceStatus.APPROVED} data-testid="invoice-filter-status-approved">{tCommon('status.approved')}</SelectItem>
            <SelectItem value={InvoiceStatus.REJECTED} data-testid="invoice-filter-status-rejected">{tCommon('status.rejected')}</SelectItem>
            <SelectItem value={InvoiceStatus.PAID} data-testid="invoice-filter-status-paid">{tCommon('status.paid')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <Label htmlFor="month-filter">{t('filters.month.label')}</Label>
        <Select value={monthFilter} onValueChange={onMonthChange}>
          <SelectTrigger id="month-filter" data-testid="invoice-filter-month">
            <SelectValue placeholder={t('filters.month.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="invoice-filter-month-all">{t('filters.month.all')}</SelectItem>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} data-testid={`invoice-filter-month-${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <Label htmlFor="member-name-filter">{t('filters.memberName.label')}</Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            id="member-name-filter"
            type="text"
            value={memberNameFilter}
            onChange={(e) => onMemberNameChange(e.target.value)}
            placeholder={t('filters.memberName.placeholder')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-8 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="invoice-filter-member-name"
          />
        </div>
      </div>

      {hasFilters && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClearFilters} data-testid="invoice-filter-clear">
            <X className="mr-2 h-4 w-4" />
            {t('filters.clear')}
          </Button>
        </div>
      )}
    </div>
  );
};
